import { createAsyncThunk } from '@reduxjs/toolkit';

import sendRequest from '../../../service/sendRequest';
import consumerApi, { AUTH_SHAPE } from '../../../service/consumerApi';
import { somethingWrongNotification } from '../../notifier/notifierActions';
import { clearConsumerSession, setConsumerSession } from '../consumerReducer';
import { ASSISTANTS_CATEGORY, GENIE_EVALUATE_TYPE, PROJECT_TYPES } from '../../../constants';
import {
  cancelEvaluationByType,
  setEvalAbortController,
  updateEvaluatedGeneratedMessages,
} from '../../genie-go/genieGoReducer';

const GET_CONSUMER_GENIE_GO_DATA = 'consumer/genieGo/GET_CONSUMER_GENIE_GO_DATA';
const CONSUMER_GENIE_GO_PREDICT_RESPONSE = 'consumer/genieGo/CONSUMER_GENIE_GO_PREDICT_RESPONSE';
const CONSUMER_GENIE_GO_REPLY = 'consumer/genieGo/CONSUMER_GENIE_GO_REPLY';
const CONSUMER_GENIE_GO_GENERATE_MESSAGES = 'consumer/genieGo/CONSUMER_GENIE_GO_GENERATE_MESSAGES';
const CONSUMER_GENIE_GO_RESET_SESSION = 'consumer/genieGo/CONSUMER_GENIE_GO_RESET_SESSION';
const EVALUATE_GENERATED_MESSAGES = 'consumer/genieGo/EVALUATE_GENERATED_MESSAGES';

export const getConsumerGenieGoData = createAsyncThunk(
  GET_CONSUMER_GENIE_GO_DATA,
  async ({ projectName, projectPassword }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/genie2go`, null, {
          authShape: AUTH_SHAPE.FLAT,
          consumerCredentials: { projectName, projectPassword },
        }),
      {
        onError: () => {
          dispatch(somethingWrongNotification());
          dispatch(clearConsumerSession());
          return rejectWithValue(null);
        },
      },
    );

    dispatch(
      setConsumerSession({
        projectType: PROJECT_TYPES.GENIE_GO,
        projectName,
        projectPassword,
      }),
    );

    return response.data;
  },
);

export const predictConsumerGenieGoResponse = createAsyncThunk(
  CONSUMER_GENIE_GO_PREDICT_RESPONSE,
  async ({ message, url }, { rejectWithValue }) => {
    const requestBody = { message, url };

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/genie2go/predict/messages`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => rejectWithValue(e),
      },
    );

    return response.data;
  },
);

export const generateConsumerGenieGoMessages = createAsyncThunk(
  CONSUMER_GENIE_GO_GENERATE_MESSAGES,
  async ({ values }, { rejectWithValue, dispatch }) => {
    const { byPass, focusOn, url, channels, fileText } = values;

    const assistantId = channels[ASSISTANTS_CATEGORY.GENERATE_ALL].assistant?.id;

    if (!assistantId || !url) {
      return rejectWithValue(null);
    }

    dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

    const requestBody = {
      assistantId,
      byPass,
      focusOn,
      url,
      fileText,
    };

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/genie2go/url`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
      },
    );

    dispatch(evaluateGeneratedMessagesConsumer({ values, messages: response.data.messages }));

    return response.data;
  },
);

export const replyConsumerGenieGo = createAsyncThunk(
  CONSUMER_GENIE_GO_REPLY,
  async ({ reply, assistantId, generateFormValues }, { rejectWithValue, getState, dispatch }) => {
    const storedThreadId = getState().genieGo.threadId;

    dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

    const requestBody = {
      reply,
      assistantId,
      threadId: storedThreadId,
    };

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/genie2go/reply`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => rejectWithValue(e),
      },
    );

    dispatch(evaluateGeneratedMessagesConsumer({ values: generateFormValues, messages: response.data.messages }));

    return response.data;
  },
);

export const resetGenieGoSession = createAsyncThunk(
  CONSUMER_GENIE_GO_RESET_SESSION,
  async (_, { rejectWithValue, getState }) => {
    const threadId = getState().genieGo.threadId;

    if (!threadId) {
      return rejectWithValue(null);
    }

    const response = await sendRequest(() => consumerApi.post(`/api/consumer/open-ai/reset?threadId=${threadId}`), {
      onError: (e) => rejectWithValue(e),
    });

    return response.data;
  },
);

export const evaluateGeneratedMessagesConsumer = createAsyncThunk(
  EVALUATE_GENERATED_MESSAGES,
  async ({ values, messages }, { rejectWithValue, dispatch }) => {
    const { url } = values;
    const requestBody = { messages, url };

    const controller = new AbortController();
    dispatch(setEvalAbortController({ controller, type: GENIE_EVALUATE_TYPE.GENERATED }));

    try {
      const response = await sendRequest(
        () =>
          consumerApi.post(`/api/consumer/genie2go/predict/bunch`, requestBody, {
            authShape: AUTH_SHAPE.PROJECT_REQUEST,
            signal: controller.signal,
          }),
        {
          onError: (e) => {
            throw new Error(e);
          },
        },
      );

      dispatch(updateEvaluatedGeneratedMessages(response.data));
      dispatch(setEvalAbortController({ controller: null, type: GENIE_EVALUATE_TYPE.GENERATED }));
    } catch (error) {
      dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

      if (controller.signal.aborted) return rejectWithValue('Cancelled');
      return rejectWithValue(error.message);
    }
  },
);
